import { ReactNode } from 'react';
import { EMPTY_CELL } from 'utils/empty-cell';
import styles from './afe-catalog-content.module.scss';

/**
 * Single-line cell renderer for a list of items. Renders the first item via
 * `renderItem` and a `+N more` suffix when the list has more than one entry.
 * The full list is exposed via the native `title` tooltip so hovering shows
 * every label. Keeps catalog rows at a uniform height regardless of count.
 */
interface CollapsedListProps<T> {
  items: T[];
  getLabel: (item: T) => string;
  renderItem: (item: T) => ReactNode;
  emptyPlaceholder?: ReactNode;
}

export function CollapsedList<T>({
  items,
  getLabel,
  renderItem,
  emptyPlaceholder = EMPTY_CELL,
}: CollapsedListProps<T>) {
  if (items.length === 0) return <>{emptyPlaceholder}</>;

  const [first, ...rest] = items;
  const tooltip = items.map(getLabel).join(', ');

  return (
    <span className={styles.collapsedEntities} title={tooltip}>
      {renderItem(first)}
      {rest.length > 0 && (
        <span className={styles.moreCount}> +{rest.length} more</span>
      )}
    </span>
  );
}
